import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMySimpleInOutBoard } from '../../api/simpleInOut';
import { useUserPrefs } from '../../hooks/useUserPrefs';
import { useOpenDirectChat } from '../../hooks/useOpenDirectChat';
import useSessionStore from '../../state/session';
import DotboardPersonRow from './DotboardPersonRow';
import {
  DOTBOARD_SORT_OPTIONS,
  DOTBOARD_STATUS_LABELS,
  DOTBOARD_STATUS_ORDER,
  buildDotboardGroups,
  countDotboardStatuses,
  dotboardAbsentStatusNote,
  dotboardChipStatuses,
  filterDotboardPeople,
  isDotboardSort,
  sortDotboardPeople,
  toDotboardPeople,
  type DotboardSort,
  type DotboardStatusFilter,
} from './dotboardModel';

const ALL_GROUPS = 'all';
const SORT_PREF = 'simpleInOut.dotboardSort';

function formatClock(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function SearchIcon() {
  return (
    <svg className="dotboard__field-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg className="dotboard__field-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 4v16M3 8l4-4 4 4M17 20V4M13 16l4 4 4-4" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="dotboard__select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * The Simple I/O dotboard (docs/simple-inout-integration.md). One instance is
 * mounted in AppShell by SimpleInOutOverlays. At 1280 px and wider the status
 * sections sit side by side (In | Out); narrower screens stack them with sticky
 * headers, and phones get the drawer full screen with Group and Sort behind a
 * filter button.
 */
export default function SimpleInOutBoardDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const filtersId = React.useId();
  const sessionEmail = useSessionStore((state) => state.profile?.email);
  const { get: getPref, set: setPref } = useUserPrefs();
  const savedSort = getPref<string>(SORT_PREF, 'name-asc');
  const sort: DotboardSort = isDotboardSort(savedSort) ? savedSort : 'name-asc';
  const [query, setQuery] = React.useState('');
  const [groupId, setGroupId] = React.useState(ALL_GROUPS);
  const [statusFilter, setStatusFilter] = React.useState<DotboardStatusFilter>('all');
  const [openPersonId, setOpenPersonId] = React.useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const { openDirectChat, opening: chatOpening } = useOpenDirectChat();

  const boardQuery = useQuery({
    queryKey: ['simple-inout', 'me', 'board'],
    queryFn: () => fetchMySimpleInOutBoard({ pageSize: 150 }),
    enabled: open,
    refetchInterval: open ? 60_000 : false,
    staleTime: 30_000,
    retry: false,
  });

  const users = React.useMemo(() => boardQuery.data?.data?.users ?? [], [boardQuery.data]);
  // "since" times and the 2-week flag are measured from the latest refresh.
  const now = React.useMemo(() => Date.now(), [boardQuery.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const people = React.useMemo(() => toDotboardPeople(users, now), [users, now]);
  const groups = React.useMemo(() => buildDotboardGroups(users), [users]);
  const matching = React.useMemo(() => filterDotboardPeople(people, query, groupId), [people, query, groupId]);
  const counts = React.useMemo(() => countDotboardStatuses(matching), [matching]);
  const chipStatuses = React.useMemo(() => dotboardChipStatuses(counts), [counts]);
  const absentNote = React.useMemo(() => dotboardAbsentStatusNote(counts), [counts]);
  const sections = React.useMemo(() => {
    const sorted = sortDotboardPeople(matching, sort);
    const statuses = statusFilter === 'all' ? DOTBOARD_STATUS_ORDER : [statusFilter];
    return statuses
      .map((status) => ({ status, people: sorted.filter((person) => person.status === status) }))
      .filter((section) => section.people.length > 0);
  }, [matching, sort, statusFilter]);

  const youEmail = String(sessionEmail || '').trim().toLowerCase();
  const inCount = counts.get('in') || 0;
  const outCount = counts.get('out') || 0;
  const cachedAt = boardQuery.data?.cached_at || boardQuery.data?.data?.meta?.trusted_cached_at || null;
  const boardEnabled = boardQuery.data?.enabled !== false;
  const boardUnavailable = boardQuery.data?.unavailable === true;
  const filtersActive = Boolean(query.trim()) || groupId !== ALL_GROUPS || statusFilter !== 'all';

  React.useEffect(function resetMissingSimpleInOutGroupFilter() {
    if (groupId === ALL_GROUPS) return;
    if (groups.some((group) => group.id === groupId)) return;
    setGroupId(ALL_GROUPS);
  }, [groups, groupId]);

  React.useEffect(function resetMissingSimpleInOutStatusChip() {
    if (statusFilter === 'all' || chipStatuses.includes(statusFilter)) return;
    setStatusFilter('all');
  }, [chipStatuses, statusFilter]);

  React.useEffect(function bindSimpleInOutBoardDrawerEscape() {
    if (!open) return undefined;
    document.body.classList.add('modal-open');
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return function cleanupSimpleInOutBoardDrawerEscape() {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, open]);

  const togglePerson = React.useCallback(function toggleDotboardPerson(personId: string) {
    setOpenPersonId((current) => (current === personId ? null : personId));
  }, []);

  const chatWith = React.useCallback(function chatWithDotboardPerson(profileId: string) {
    void openDirectChat(profileId, onClose);
  }, [openDirectChat, onClose]);

  function clearFilters() {
    setQuery('');
    setGroupId(ALL_GROUPS);
    setStatusFilter('all');
  }

  if (!open) return null;

  let body: React.ReactNode;
  if (boardQuery.isLoading) {
    body = (
      <div className="simple-inout-empty">
        <strong>Loading dotboard</strong>
        <span>Fetching current Simple I/O statuses.</span>
      </div>
    );
  } else if (boardQuery.isError) {
    body = (
      <div className="simple-inout-empty" data-state="error">
        <strong>Dotboard failed</strong>
        <span>{boardQuery.error instanceof Error ? boardQuery.error.message : 'Failed to load Simple I/O dotboard'}</span>
      </div>
    );
  } else if (!boardEnabled) {
    body = (
      <div className="simple-inout-empty">
        <strong>Dotboard unavailable</strong>
        <span>Simple I/O is not verified for dashboard users.</span>
      </div>
    );
  } else if (boardUnavailable) {
    body = (
      <div className="simple-inout-empty" data-state="error">
        <strong>Dotboard unavailable</strong>
        <span>{boardQuery.data?.last_error?.status === 429 ? 'Simple I/O is rate limited. Try refresh again after it resets.' : 'Simple I/O could not return users right now.'}</span>
      </div>
    );
  } else if (!sections.length) {
    body = (
      <div className="simple-inout-empty dotboard__no-match">
        <strong>No one matches</strong>
        <span>Try a first name, a site, or another group.</span>
        {filtersActive ? (
          <button type="button" className="btn btn-outline" onClick={clearFilters}>Clear filters</button>
        ) : null}
      </div>
    );
  } else {
    body = (
      <div className="dotboard__sections">
        {sections.map((section) => (
          <section key={section.status} className="dotboard__section" aria-label={DOTBOARD_STATUS_LABELS[section.status]}>
            <h3 className="dotboard__section-head">
              <span className="dotboard-dot" data-status={section.status} aria-hidden="true" />
              {DOTBOARD_STATUS_LABELS[section.status]}
              <span className="dotboard__section-count">{section.people.length}</span>
            </h3>
            <ul className="dotboard__list">
              {section.people.map((person) => (
                <DotboardPersonRow
                  key={person.id}
                  person={person}
                  isYou={Boolean(youEmail) && person.email.toLowerCase() === youEmail}
                  expanded={openPersonId === person.id}
                  now={now}
                  chatOpening={chatOpening}
                  onToggle={togglePerson}
                  onChat={chatWith}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return createPortal(
    <div
      className="modal-overlay dotboard-overlay"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="dotboard"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dotboard__head">
          <div className="dotboard__title-block">
            <span className="dotboard__eyebrow">Simple I/O</span>
            <h2 id={titleId} className="dotboard__title">Dotboard</h2>
            {boardQuery.data?.stale ? (
              <span className="dotboard__live" data-state="stale">
                Simple I/O didn’t answer · showing the {formatClock(cachedAt)} copy
              </span>
            ) : cachedAt ? (
              <span className="dotboard__live">Live · updated {formatClock(cachedAt)}</span>
            ) : null}
          </div>
          <div className="dotboard__head-actions">
            <button
              type="button"
              className="dotboard-icon-btn"
              aria-label={boardQuery.isFetching ? 'Refreshing dotboard' : 'Refresh dotboard'}
              disabled={boardQuery.isFetching}
              onClick={() => void boardQuery.refetch()}
            >
              <svg className={boardQuery.isFetching ? 'dotboard__spin' : undefined} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" />
              </svg>
            </button>
            <button type="button" className="dotboard-icon-btn" aria-label="Close dotboard" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </header>

        <div className="dotboard__toolbar" data-filters-open={filtersOpen ? 'true' : undefined}>
          <label className="dotboard__search">
            <span className="sr-only">Find a person or place</span>
            <SearchIcon />
            <input
              type="search"
              value={query}
              placeholder="Find a person or place"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="dotboard-icon-btn dotboard__filters-toggle"
            aria-expanded={filtersOpen}
            aria-controls={filtersId}
            aria-label="Group and sort"
            onClick={() => setFiltersOpen((current) => !current)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
          </button>
          <div id={filtersId} className="dotboard__filters">
            <label className="dotboard__select">
              <span className="sr-only">Group</span>
              <select
                value={groupId}
                onChange={(event) => setGroupId(event.target.value)}
                disabled={!groups.length}
              >
                <option value={ALL_GROUPS}>All groups</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.count})
                  </option>
                ))}
              </select>
              <ChevronIcon />
            </label>
            <label className="dotboard__select dotboard__select--sort">
              <span className="sr-only">Sort</span>
              <SortIcon />
              <select value={sort} onChange={(event) => setPref(SORT_PREF, event.target.value)}>
                {DOTBOARD_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <ChevronIcon />
            </label>
          </div>
        </div>

        <div className="dotboard__chips" role="group" aria-label="Filter by status">
          <button
            type="button"
            className="dotboard__chip"
            aria-pressed={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="dotboard__chip-count">{matching.length}</span>
          </button>
          {chipStatuses.map((status) => (
            <button
              key={status}
              type="button"
              className="dotboard__chip"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
            >
              <span className="dotboard-dot" data-status={status} aria-hidden="true" />
              {DOTBOARD_STATUS_LABELS[status]} <span className="dotboard__chip-count">{counts.get(status) || 0}</span>
            </button>
          ))}
          {absentNote ? <span className="dotboard__absent">{absentNote}</span> : null}
        </div>

        <div className="dotboard__ratio" role="img" aria-label={`${inCount} in, ${outCount} out`}>
          <span className="dotboard__ratio-in" style={{ flexGrow: inCount }} />
          <span className="dotboard__ratio-out" style={{ flexGrow: outCount }} />
        </div>

        <div className="dotboard__body">{body}</div>

        <footer className="dotboard__foot">Refreshes every minute while open</footer>
      </aside>
    </div>,
    document.body,
  );
}
